import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTableColumns,
  faEnvelope,
  faCalendarCheck,
  faUserPlus,
  faRightFromBracket,
  faBars,
  faXmark,
  faInbox,
  faRotate,
  faEye,
  faCheck,
  faBan,
  faTrash,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../Images/logo.jpg";
import "../Styles/Admin.css";

const TABS = [
  { key: "overview", label: "Overview", icon: faTableColumns },
  { key: "contacts", label: "Contacts", icon: faEnvelope },
  { key: "appointments", label: "Appointments", icon: faCalendarCheck },
  { key: "applications", label: "Applications", icon: faUserPlus },
];

function fmtDate(s) {
  if (!s) return "-";
  const d = new Date(s.replace(" ", "T") + "Z");
  if (isNaN(d)) return s;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const StatusBadge = ({ status }) => {
  const s = status || "new";
  return <span className={`status-badge status-${s}`}>{s}</span>;
};

function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null); // { type, row }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/submissions", {
        credentials: "include",
      });
      if (res.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setData(await res.json());
    } catch {
      setData({ contacts: [], appointments: [], applications: [], counts: {} });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetch("/api/admin/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setEmail(d.email || ""))
      .catch(() => navigate("/admin/login", { replace: true }));
    load();
  }, [navigate, load]);

  const logout = async () => {
    await fetch("/api/admin/logout", { method: "POST", credentials: "include" });
    navigate("/admin/login", { replace: true });
  };

  // update status locally + on server
  const setStatus = async (type, id, status) => {
    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      next[type] = prev[type].map((r) => (r.id === id ? { ...r, status } : r));
      return next;
    });
    setDetail((d) => (d && d.row.id === id ? { ...d, row: { ...d.row, status } } : d));
    await fetch("/api/admin/status", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ type, id, status }),
    });
  };

  const removeRow = async (type, id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      next[type] = prev[type].filter((r) => r.id !== id);
      next.counts = { ...prev.counts, [type]: (next[type] || []).length };
      return next;
    });
    setDetail(null);
    await fetch("/api/admin/delete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ type, id }),
    });
  };

  const counts = data?.counts || {};
  const go = (key) => {
    setTab(key);
    setSidebarOpen(false);
    setSearch("");
    setFilter("all");
  };

  // filter + search for the active table
  const rows = useMemo(() => {
    if (!data || tab === "overview") return [];
    let list = data[tab] || [];
    if (filter !== "all") list = list.filter((r) => (r.status || "new") === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        JSON.stringify(r).toLowerCase().includes(q)
      );
    }
    return list;
  }, [data, tab, filter, search]);

  return (
    <div className="admin-shell">
      <aside className={`admin-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="admin-brand">
          <img src={logo} alt="Harmony Rise" />
          <span className="admin-brand-text">
            Harmony Rise
            <small>Admin</small>
          </span>
        </div>
        <nav className="admin-nav">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={tab === t.key ? "active" : ""}
              onClick={() => go(t.key)}
            >
              <FontAwesomeIcon icon={t.icon} /> {t.label}
              {t.key !== "overview" && counts[t.key] != null && (
                <span className="badge">{counts[t.key]}</span>
              )}
            </button>
          ))}
        </nav>
        <button className="admin-logout" onClick={logout}>
          <FontAwesomeIcon icon={faRightFromBracket} /> Log Out
        </button>
      </aside>

      <div
        className={`admin-overlay ${sidebarOpen ? "show" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      <div className="admin-main">
        <header className="admin-topbar">
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <button
              className="admin-hamburger"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle menu"
            >
              <FontAwesomeIcon icon={sidebarOpen ? faXmark : faBars} />
            </button>
            <div className="admin-title-block">
              <h1>{TABS.find((t) => t.key === tab)?.label}</h1>
              <p className="admin-subtitle">
                {tab === "overview"
                  ? "Welcome back — here's what's happening."
                  : `Manage your ${tab}.`}
              </p>
            </div>
          </div>
          <div className="admin-user">
            <button
              onClick={load}
              title="Refresh"
              style={{
                background: "none",
                border: "none",
                color: "var(--teal)",
                cursor: "pointer",
                fontSize: "1rem",
              }}
            >
              <FontAwesomeIcon icon={faRotate} />
            </button>
            <span>{email}</span>
            <span className="admin-avatar">
              {email ? email[0].toUpperCase() : "A"}
            </span>
          </div>
        </header>

        <div className="admin-content">
          {loading ? (
            <div className="admin-loading">Loading submissions…</div>
          ) : tab === "overview" ? (
            <Overview counts={counts} data={data} onGo={go} />
          ) : (
            <>
              <div className="admin-toolbar">
                <div className="admin-search">
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                  <input
                    type="text"
                    placeholder="Search…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="admin-filters">
                  {["all", "new", "accepted", "rejected"].map((f) => (
                    <button
                      key={f}
                      className={`admin-filter ${filter === f ? "active" : ""}`}
                      onClick={() => setFilter(f)}
                    >
                      {f[0].toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <DataTable
                type={tab}
                rows={rows}
                onView={(row) => setDetail({ type: tab, row })}
                onStatus={setStatus}
                onDelete={removeRow}
              />
            </>
          )}
        </div>
      </div>

      {detail && (
        <DetailModal
          type={detail.type}
          row={detail.row}
          onClose={() => setDetail(null)}
          onStatus={setStatus}
          onDelete={removeRow}
        />
      )}
    </div>
  );
}

/* ---------- Overview ---------- */
function Overview({ counts, data, onGo }) {
  const recent = [
    ...(data?.contacts || []).map((r) => ({ type: "Contact", who: r.name, when: r.created_at, status: r.status })),
    ...(data?.appointments || []).map((r) => ({ type: "Appointment", who: `${r.first_name} ${r.last_name}`, when: r.created_at, status: r.status })),
    ...(data?.applications || []).map((r) => ({ type: "Application", who: r.full_name, when: r.created_at, status: r.status })),
  ]
    .sort((a, b) => (a.when < b.when ? 1 : -1))
    .slice(0, 10);

  return (
    <>
      <div className="admin-stats">
        <button className="admin-stat" onClick={() => onGo("contacts")}>
          <span className="stat-icon"><FontAwesomeIcon icon={faEnvelope} /></span>
          <div><h3>{counts.contacts || 0}</h3><span>Contact Messages</span></div>
        </button>
        <button className="admin-stat" onClick={() => onGo("appointments")}>
          <span className="stat-icon"><FontAwesomeIcon icon={faCalendarCheck} /></span>
          <div><h3>{counts.appointments || 0}</h3><span>Appointment Requests</span></div>
        </button>
        <button className="admin-stat" onClick={() => onGo("applications")}>
          <span className="stat-icon"><FontAwesomeIcon icon={faUserPlus} /></span>
          <div><h3>{counts.applications || 0}</h3><span>Job Applications</span></div>
        </button>
      </div>

      <div className="admin-card">
        <div className="admin-card-head"><h2>Recent Activity</h2></div>
        {recent.length === 0 ? (
          <div className="admin-empty">
            <FontAwesomeIcon icon={faInbox} size="2x" />
            <p style={{ marginTop: "12px" }}>No submissions yet.</p>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>Type</th><th>Name</th><th>Status</th><th>Received</th></tr>
              </thead>
              <tbody>
                {recent.map((r, i) => (
                  <tr key={i}>
                    <td>{r.type}</td>
                    <td>{r.who}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="cell-muted">{fmtDate(r.when)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/* ---------- shared table ---------- */
const Empty = () => (
  <div className="admin-empty">
    <FontAwesomeIcon icon={faInbox} size="2x" />
    <p style={{ marginTop: "12px" }}>No records found.</p>
  </div>
);

const COLUMNS = {
  contacts: [
    { h: "Name", get: (r) => r.name },
    { h: "Email", get: (r) => r.email },
    { h: "Subject", get: (r) => r.subject || "-" },
  ],
  appointments: [
    { h: "Name", get: (r) => `${r.first_name} ${r.last_name}` },
    { h: "Phone", get: (r) => r.phone },
    { h: "Service", get: (r) => r.service || "-" },
    { h: "Date", get: (r) => r.appt_date || "-" },
  ],
  applications: [
    { h: "Name", get: (r) => r.full_name },
    { h: "Phone", get: (r) => r.cell_phone || r.home_phone || "-" },
    { h: "City/State", get: (r) => [r.city, r.state].filter(Boolean).join(", ") || "-" },
  ],
};

function DataTable({ type, rows, onView, onStatus, onDelete }) {
  const cols = COLUMNS[type];
  return (
    <div className="admin-card">
      <div className="admin-card-head">
        <h2>{type[0].toUpperCase() + type.slice(1)}</h2>
        <span className="cell-muted">{rows.length} shown</span>
      </div>
      {rows.length === 0 ? (
        <Empty />
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.h}>{c.h}</th>
                ))}
                <th>Status</th>
                <th>Received</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => onView(r)}>
                  {cols.map((c) => (
                    <td key={c.h}>{c.get(r)}</td>
                  ))}
                  <td><StatusBadge status={r.status} /></td>
                  <td className="cell-muted">{fmtDate(r.created_at)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="row-actions">
                      <button className="row-btn view" title="View" onClick={() => onView(r)}>
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button className="row-btn accept" title="Accept" onClick={() => onStatus(type, r.id, "accepted")}>
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                      <button className="row-btn reject" title="Reject" onClick={() => onStatus(type, r.id, "rejected")}>
                        <FontAwesomeIcon icon={faBan} />
                      </button>
                      <button className="row-btn delete" title="Delete" onClick={() => onDelete(type, r.id)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- detail modal ---------- */
const FIELD_LABELS = {
  contacts: [
    ["name", "Name"], ["email", "Email"], ["subject", "Subject"], ["message", "Message"],
  ],
  appointments: [
    ["first_name", "First Name"], ["last_name", "Last Name"], ["email", "Email"],
    ["phone", "Phone"], ["dob", "Date of Birth"], ["gender", "Gender"],
    ["address", "Address"], ["city", "City"], ["state", "State"], ["postal_code", "Postal Code"],
    ["service", "Service"], ["is_returning", "Returning Client"], ["appt_date", "Appointment Date"],
    ["appt_time", "Preferred Time"], ["description", "Description"],
  ],
  applications: [
    ["full_name", "Full Name"], ["application_date", "Application Date"],
    ["street_address", "Street Address"], ["city", "City"], ["state", "State"], ["zip", "ZIP"],
    ["home_phone", "Home Phone"], ["cell_phone", "Cell Phone"], ["dob", "Date of Birth"],
    ["smokes", "Smokes"], ["heard_about", "Heard About Us"],
    ["school", "School"], ["degree", "Degree"], ["course", "Course"], ["edu_dates", "Education Dates"],
    ["skills", "Skills"], ["hours_available", "Hours/Week"], ["days_available", "Days Available"],
    ["emergency_available", "Emergency Available"], ["has_transport", "Transport"],
    ["work_authorized", "Work Authorized"], ["work_history", "Work History"],
    ["references_list", "References"],
  ],
};

function DetailModal({ type, row, onClose, onStatus, onDelete }) {
  const fields = FIELD_LABELS[type] || [];
  const title =
    type === "contacts"
      ? row.name
      : type === "appointments"
      ? `${row.first_name} ${row.last_name}`
      : row.full_name;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>
            {title} <StatusBadge status={row.status} />
          </h2>
          <button className="modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="modal-body">
          {fields.map(([key, label]) => {
            const val = row[key];
            if (val === undefined || val === null || val === "") return null;
            return (
              <div className="detail-row" key={key}>
                <div className="dl">{label}</div>
                <div className="dv">
                  {key === "email" ? (
                    <a href={`mailto:${val}`}>{val}</a>
                  ) : key.includes("phone") ? (
                    <a href={`tel:${val}`}>{val}</a>
                  ) : (
                    String(val)
                  )}
                </div>
              </div>
            );
          })}
          <div className="detail-row">
            <div className="dl">Received</div>
            <div className="dv">{fmtDate(row.created_at)}</div>
          </div>
        </div>
        <div className="modal-foot">
          <button className="hr-btn btn-accept" onClick={() => onStatus(type, row.id, "accepted")}>
            <FontAwesomeIcon icon={faCheck} /> Accept
          </button>
          <button className="hr-btn btn-reject" onClick={() => onStatus(type, row.id, "rejected")}>
            <FontAwesomeIcon icon={faBan} /> Reject
          </button>
          <button className="hr-btn btn-del" onClick={() => onDelete(type, row.id)}>
            <FontAwesomeIcon icon={faTrash} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
